import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Dialog, DialogTitle, DialogContent, Typography, DialogActions } from '@mui/material';
import { theme } from '../../../../../theme';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EmailTemplate } from '../ManageEmailTemplates/ManageEmailTemplates';
import { sanitizeContent } from '../../../../Common/Sanitize/sanitizeReactQuill';




interface EmailConfigurationProps {
  emailList: string;
  setEmailList: (value: string) => void;
  message: string;
  setMessage: (value: string) => void;
  handleSave: (name: string, subject: string, body: string, emailNum: number | null) => void;
  handleDelete: (id: number | null) => void;
  setIsMessageConfigured: (value: boolean) => void; // New prop to manage configuration state
  emailTemplates: EmailTemplate[];
  selectedEmailId:number | null;
  setSelectedEmailId:(value: number | null) => void;
  setSubject:(value : string) => void;
  subject:string
}

const EmailConfiguration: React.FC<EmailConfigurationProps> = ({
  emailList,
  setEmailList,
  message,
  setMessage,
  handleSave,
  handleDelete,
  setIsMessageConfigured, // Destructure the new prop
  emailTemplates = [],
  selectedEmailId,
  setSelectedEmailId,
  setSubject,
  subject
}) => {
  // const [subject, setSubject] = useState('');
  // const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null); // State for selected email ID
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const quillRef = useRef<ReactQuill>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isUserTemplate, setIsUserTemplate] = useState(false)

  const handleSaveEmail = async () => {
    const newErrors: { [key: string]: string } = {};
    if (subject.trim().length === 0) {
      newErrors.subject = 'Email subject is required.'
    }
    const sanitizedValue = sanitizeContent(message)
    if (!sanitizedValue) {
      newErrors.message = 'Email body is required.'
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return
    }
    await handleSave(emailList, subject, message, selectedEmailId)
    setEmailList('');
    setSubject('');
    setMessage('');
    setSelectedEmailId(null);
    setIsMessageConfigured(false);
  }

  const handleEmailListChange = (event: any) => {
    const newValue = event.target.value;
    setEmailList(newValue);

    // Find the selected template from the messageTemplates array
    const selectedTemplate = emailTemplates.find(
      (template) => template.EmailSubject === newValue
    );

    // If template found, set the respective states
    if (selectedTemplate) {
      setSelectedEmailId(selectedTemplate.EmailNum)
      setSubject(selectedTemplate.EmailSubject);
      setMessage(selectedTemplate.EmailBody);
      setIsUserTemplate(selectedTemplate.IsUserTemplate)
      setIsMessageConfigured(true)
      // setSelectedUserList(selectedTemplate["UserList"] || []); // Set the user list if present
    } else {
      setSubject('');
      setMessage('');
      setSelectedEmailId(null); // Reset selected email ID
      setIsMessageConfigured(false);
      setIsUserTemplate(false)
    }
  };
  const handleClear = () => {
    setEmailList('');
    setSubject('');
    setMessage('');
    setSelectedEmailId(null); // Reset selected email ID
    setIsMessageConfigured(false); // Reset the configuration state when cleared
    setIsUserTemplate(false)
  };


  const handleDeleteEmail = async () => {
    await handleDelete(selectedEmailId)
    setEmailList('');
    setSubject('');
    setMessage('');
    setSelectedEmailId(null);
    setIsMessageConfigured(false)
    setOpenDeleteDialog(false)
    setIsUserTemplate(false)
  }

  const handleCancelLogout = () => {
    setOpenDeleteDialog(false);
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'], // Bold, Italic, Underline
      [{ 'list': 'ordered' }, { 'list': 'bullet' }], // Bullet points and numbered points
      ['link'], // Link
      [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }], // Alignment
      [{ 'indent': '-1' }, { 'indent': '+1' }], // Outdent and Indent
    ],
    clipboard: {
      matchVisual: false, // Prevents Quill from auto-converting visual line breaks to `<p><br></p>`
    },
  };

  const handleInputChange = (value: string, field: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
    switch (field) {
      case "subject":
        setSubject(value);
        break;
      case "message":
        setMessage(value);
        break;
      default:
        break;
    }
  };


  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2.2}
      sx={{


        p: '1vw',
        pb: 0,
        backgroundColor: 'background.paper',
        borderRadius: '1vw',
        border: '0.0694vw solid #E4E7EC',
      }}
    >

      <Box sx={{ display: 'flex', flexDirection: 'column' }} gap={2}>
        <FormControl size="small" sx={{ mb: '1vw', width: '100%' }}>
          <InputLabel id="email-list-label">Configured Email List</InputLabel>
          <Select
            labelId="email-list-label"
            id="email-list"
            value={emailList}
            onChange={handleEmailListChange}
            label="Configured email List"
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: '20vw', // You can adjust this as needed
                },
              },
            }}
          >

            {Array.isArray(emailTemplates) && emailTemplates.length > 0 ? (
              emailTemplates.map((template) => (
                <MenuItem key={template.EmailNum} value={template.EmailSubject}>
                  {template.EmailSubject}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No templates available</MenuItem>
            )}
          </Select>
        </FormControl>


        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => handleInputChange(e.target.value, "subject")}
          variant="outlined"
          size="small"
          sx={{ mb: '1vw' }}
          error={!!errors.subject}
          helperText={errors.subject}
          required
          autoComplete='off'
        />
      </Box>

      <Typography
        variant="caption"
      >
        Body Content <span style={{ color: theme.palette.error.main }}>* </span>
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '2vw' }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={message}
          onChange={(value) => handleInputChange(value, "message")}
          modules={quillModules}
          style={{ height: '20vh', marginBottom: '2vh' }}
        />
        {errors.message && (
          <Typography color="error" variant="body2" sx={{ mt: 4, ml: 2 }}>
            {errors.message}
          </Typography>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        sx={{
          borderBottomLeftRadius: '1.11vw',
          borderBottomRightRadius: '1.11vw',
          backgroundColor: theme.palette.primary.light,
          p: '1vw 3vw', // Padding adjusted to match the container
          width: 'calc(100% + 2vw)',
          position: 'relative',
          right: '1vw', // Reset any offset
        }}
      >
        <Button variant="outlined" color="secondary" onClick={handleClear} sx={{ minWidth: '100px' }}>
          Clear
        </Button>
        {(selectedEmailId && isUserTemplate) && (
          <Button
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              minWidth: '100px',
            }}
            onClick={() => { setOpenDeleteDialog(true) }}
          >
            Delete
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveEmail}
          sx={{
            minWidth: '100px',
          }}
        >
          Save
        </Button>
      </Box>

      <Dialog open={openDeleteDialog} onClose={handleCancelLogout}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this template? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEmail}

            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default EmailConfiguration;
